/**
 * EventBus - Centralized event system for module communication
 * Allows loose coupling between game systems through pub/sub pattern
 */

class EventBus {
  constructor() {
    this.events = {};
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Handler function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Subscribe to an event once (auto-unsubscribes after first call)
   * @param {string} event - Event name
   * @param {Function} callback - Handler function
   */
  once(event, callback) {
    const wrapper = (data) => {
      this.off(event, wrapper);
      callback(data);
    };
    this.on(event, wrapper);
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} callback - Handler function to remove
   */
  off(event, callback) {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(cb => cb !== callback);
  }

  /**
   * Emit an event to all subscribers
   * @param {string} event - Event name
   * @param {*} data - Data to pass to handlers
   */
  emit(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event handler for "${event}":`, error);
      }
    });
  }

  /**
   * Clear all event listeners
   */
  clear() {
    this.events = {};
  }

  /**
   * Clear listeners for a specific event
   * @param {string} event - Event name
   */
  clearEvent(event) {
    delete this.events[event];
  }
}

// Singleton instance
export const eventBus = new EventBus();

// Event type constants for type safety and discoverability
export const GameEvents = {
  // Fish events
  FISH_CAUGHT: 'fish:caught',
  FISH_ESCAPED: 'fish:escaped',
  FISH_BITING: 'fish:biting',

  // Fishing events
  FISHING_STARTED: 'fishing:started',
  FISHING_ENDED: 'fishing:ended',
  FISHING_CAST: 'fishing:cast',

  // Minigame events
  MINIGAME_STARTED: 'minigame:started',
  MINIGAME_HIT: 'minigame:hit',
  MINIGAME_MISS: 'minigame:miss',
  MINIGAME_SUCCESS: 'minigame:success',
  MINIGAME_FAILURE: 'minigame:failure',

  // Player events
  PLAYER_MOVED: 'player:moved',
  PLAYER_STOPPED: 'player:stopped',
  PLAYER_POSITION_CHANGED: 'player:position:changed',

  // House events
  HOUSE_ENTERED: 'house:entered',
  HOUSE_EXITED: 'house:exited',
  TRANSITION_STARTED: 'transition:started',
  TRANSITION_ENDED: 'transition:ended',

  // UI events
  INVENTORY_OPENED: 'ui:inventory:opened',
  INVENTORY_CLOSED: 'ui:inventory:closed',
  NOTIFICATION_SHOWN: 'ui:notification:shown',

  // Weather events
  WEATHER_CHANGED: 'weather:changed',

  // Game state events
  GAME_STARTED: 'game:started',
  GAME_PAUSED: 'game:paused',
  GAME_RESUMED: 'game:resumed',
  SCORE_CHANGED: 'score:changed',

  // Achievement events
  ACHIEVEMENT_UNLOCKED: 'achievement:unlocked',
  ACHIEVEMENT_PROGRESS: 'achievement:progress',

  // Progression events
  AREA_UNLOCKED: 'progression:area:unlocked',
  ROD_UPGRADED: 'progression:rod:upgraded',

  // Tutorial events
  TUTORIAL_STEP: 'tutorial:step',
  TUTORIAL_COMPLETED: 'tutorial:completed',
  TUTORIAL_SKIPPED: 'tutorial:skipped'
};

export default EventBus;
